/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import VoiceRecorder from "../components/VoiceRecorder";
import { useAuth } from "../auth/AuthProvider";
import { api } from '../api';
import { Box, Button, Typography, Paper } from "@mui/material";
import RequireRole from "../components/RequireRole";

export default function VoiceSubmission() {
  const { token } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [transcript, setTranscript] = useState("");
  const navigate = useNavigate();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [promptOverride, setPromptOverride] = useState("");
  const [showExamples, setShowExamples] = useState(false);

  const handleVoiceSubmit = async (audioBlob) => {
  setStatus("submitting");

  try {
    const formData = new FormData();
    formData.append("voice_file", audioBlob, "voice.webm");
    if (promptOverride.trim()) {
      formData.append("prompt_override", promptOverride)
    }
    // formData.append("lpo_id", user?.sub); // ✅ Stable user ID from IdP

    const res = await api.post("/secure/voice-submissions", formData); // ✅ No headers needed!

    setTranscript(res.data.transcript || "Voice submitted successfully.");
    setStatus("success");
    // Delay briefly before redirect
    setTimeout(() => navigate("/secure/my-insights"), 1500);
  } catch (err) {
    console.error("❌ Voice submission failed:", err);
    setStatus("error");
  }
};

  return (
    <RequireRole roles={["field_agent", "reviewer", "admin"]}>
    <div>
      <h1>🎤 Submit Field Insight</h1>
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            mb: 2,
            p: 2,
            backgroundColor: "#f7f7f7",
            borderRadius: 2,
          }}
        >
        <Typography variant="subtitle2" gutterBottom>
          What can you use a voice note for?
        </Typography>

        <Typography variant="body2" color="text.secondary">
          You can speak naturally about:
        </Typography>

        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
          <li>Supplier discovery and pricing</li>
          <li>Market or competitor price intelligence</li>
          <li>Customer requirements or feedback</li>
          <li>Quality issues or incidents</li>
          <li>Inventory or capacity updates</li>
        </Box>

        <Typography variant="body2" color="text.secondary">
          The system will structure it automatically — you can review and correct it later.
        </Typography>

        <Button
          size="small"
          variant="text"
          sx={{ mt: 1, px: 0 }}
          onClick={() => setShowExamples(!showExamples)}
        >
          {showExamples ? "Hide examples" : "See examples"}
        </Button>

        {showExamples && (
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary">
              Examples:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
              <li>
                “Inspected the primary machine on the floor today. Vibration levels were higher than normal, and operating temperature was consistently around 15 percent above the usual range. 
                Output quality was still within tolerance, but there were intermittent slowdowns during the shift. Maintenance estimated that without intervention, failure risk could increase over the next two to three weeks. 
                They recommended a planned shutdown of one day to replace a worn component.”
              </li>
              <li>
                “Met with a new supplier today. They can supply 5,000 units per month at a unit price of 42, with a minimum commitment of three months. 
                Current lead time is two weeks, but they mentioned it could extend to four weeks during peak demand. Initial quality looks acceptable, but they suggested a trial batch before scaling.”
              </li>
              <li>
                “Customer shared feedback on pricing and competition. They are currently buying from us at 48 per unit, but said two other vendors are offering 
                similar specs at around 43 to 45 per unit. However, those vendors have longer delivery times, about three to four weeks instead of our two. 
                Customer wants a revised quote by Friday and plans to finalize the decision early next week.”
              </li>                
              <li>
                “Supplier informed us of a potential risk. They expect capacity to drop by roughly 20 percent next month due to scheduled maintenance, 
                which could reduce monthly supply from 6,000 units to about 4,800. Inventory on hand covers roughly two weeks of production, so if maintenance overruns, 
                we may face delays unless we line up an alternate source.”
              </li>
            </Box>
          </Box>
        )}
      </Paper>

      <VoiceRecorder onRecordingComplete={handleVoiceSubmit} />
      <Box mt={2}>
      <Button
        size="small"
        onClick={() => setShowAdvanced(!showAdvanced)}
        variant="text"
      >
        {showAdvanced ? "Hide Advanced Settings" : "Show Advanced Settings"}
      </Button>

      {showAdvanced && (
        <Box mt={1}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Override default LLM prompt (optional)
          </Typography>
          <textarea
            rows={6}
            style={{ width: "100%", borderRadius: 8, padding: 8 }}
            placeholder="Custom prompt to guide the AI extraction..."
            value={promptOverride}
            onChange={(e) => setPromptOverride(e.target.value)}
          />
        </Box>
      )}
    </Box>
      <Button
        component={Link}
        to="/secure/my-insights"
        variant="outlined"
        sx={{ mt: 2 }}
        >
          View My Insights
      </Button>

      {status === "submitting" && <p>⏳ Transcribing and parsing...</p>}
      {status === "success" && <p>✅ Done: {transcript}</p>}
      {status === "error" && <p>❌ Something went wrong. Try again.</p>}
    </div>
    </RequireRole>
  );
}